// @ts-nocheck
import { createHash } from 'crypto';

import { LRUCache } from 'lru-cache';
import type * as RCL from 'rclnodejs';

import type { CartesianPose } from '@sb/geometry';
import { ros2TooltipToTarget, targetToROS2Tooltip } from '@sb/geometry';
import { makeNamespacedLog } from '@sb/log';
import type {
  ForwardKinematicsRequest,
  ForwardKinematicsResponse,
  FrameOfReference,
  InverseKinematicsRequest,
  InverseKinematicsSolution,
} from '@sb/motion-planning';
import { ArmJointPositions } from '@sb/motion-planning';
import type { CartesianPlanningActionRequest } from '@sb/ros/CartesianPlanningAction';
import type { TaskFromRobotGoalActionRequest } from '@sb/ros/TaskFromRobotGoalAction';
import { TaskFromRobotGoalAction } from '@sb/ros/TaskFromRobotGoalAction';
import { TaskFromRobotGoalActionResult } from '@sb/ros/types/TaskFromRobotGoalActionResult';
import type { CacheBackend } from '@sb/utilities/src/ReadThroughCache';
import { ReadThroughCacheAsync } from '@sb/utilities/src/ReadThroughCacheAsync';

import { CartesianPlanningAction } from './CartesianPlanningAction';
import { JOINT_NAMES } from './constants';
import { ActionCancelledError } from './errors/ActionCancelledError';
import { FKCheckService } from './FKCheckService';
import {
  getMotionPlanBucketKey,
  getRosMotionPlanCacheKey,
} from './getRosMotionPlanCacheyKey';
import {
  GetToolDirectionsService,
  type FrameDirections,
} from './GetToolDirectionsService';
import { IKCollisionCheckService } from './IKCollisionCheckService';
import { JoggingAction, type JoggingActionRequest } from './JoggingAction';
import { RecoveryService } from './RecoveryService';
import type { CallableCancellableAction } from './types/CallableCancellableAction';

const log = makeNamespacedLog('RosMotionPlanner');

// Per-joint tolerance (radians) used for two purposes that must stay in sync:
//   1. accepting a bucket-cached plan whose start is within tolerance of the
//      requested start (see areJointStatesWithinTolerance), and
//   2. the no-op hold threshold in validateServedPlan.
// Both read the SAME value (the instance's `startJointTolerance`, which defaults
// here) so a move small enough to be served a mis-anchored cached plan is also
// small enough to be held in place instead. Configurable per instance via the
// constructor `startJointTolerance` option.
export const DEFAULT_START_JOINT_TOLERANCE = 0.005;

const DEFAULT_BUCKET_SIZE = 1000;
const DEFAULT_BUCKET_ENTRY_CAPACITY = 1000;

// A served (cached or bucket-matched) plan must begin at the requested start
// AND end at the requested goal. A stale or corrupt cache entry whose trajectory
// diverges from the request would command a discontinuous motion when executed
// (the vizbot "snap"; on hardware, an unsafe move), so we reject it and re-plan
// fresh. Legitimate bucket reuse stays within ~2x DEFAULT_START_JOINT_TOLERANCE
// (0.01); a stale-plan divergence is on the order of radians.
const SERVED_PLAN_DIVERGENCE_TOLERANCE = 0.05;

// Start steps at or below this (radians) are treated as anchored (float/rounding
// noise). A served plan's first point is "off-anchor" only when its divergence
// from the live current position exceeds this. Fresh plans start exactly at the
// requested start, so they sit well under this.
const START_STEP_ANCHORED_FLOOR_RAD = 1e-4;

type MotionPlanBucketEntry = {
  cacheKey: string;
  request: MotionPlanCacheRequest;
};

type MotionPlanBucketOptions = {
  bucketSize?: number;
  startJointTolerance?: number;
  isWithinStartJointTolerance?: (
    cachedStart: number[],
    requestedStart: number[],
  ) => boolean;
};

type MotionPlanCacheRequest = RosMotionPlannerRequest & {
  useScalingForSpeedLimits: boolean;
};

export type RosMotionPlannerRequest = (
  | TaskFromRobotGoalActionRequest
  | CartesianPlanningActionRequest
) & { useCartesianPlanner: boolean };

export class RosMotionPlanner {
  private motionPlanCache: ReadThroughCacheAsync<
    RosMotionPlannerRequest,
    TaskFromRobotGoalActionResult
  > | null = null;

  private motionPlanBucketCache: LRUCache<
    string,
    LRUCache<string, MotionPlanBucketEntry>
  > | null = null;

  private readonly isWithinStartJointTolerance: (
    cachedStart: number[],
    requestedStart: number[],
  ) => boolean;

  // The per-joint start tolerance this instance actually uses. Shared by the
  // bucket start-match and the no-motion short-circuit so they never diverge.
  private readonly startJointTolerance: number;

  private bucketGetCount = 0;

  private bucketHitCount = 0;

  public constructor(
    motionPlanCacheBackend: CacheBackend<TaskFromRobotGoalActionResult> | null = null,
    options: MotionPlanBucketOptions = {},
  ) {
    const startJointTolerance =
      options.startJointTolerance ?? DEFAULT_START_JOINT_TOLERANCE;

    this.startJointTolerance = startJointTolerance;

    this.isWithinStartJointTolerance =
      options.isWithinStartJointTolerance ??
      ((cachedStart, requestedStart) =>
        this.areJointStatesWithinTolerance(
          cachedStart,
          requestedStart,
          startJointTolerance,
        ));

    if (motionPlanCacheBackend) {
      this.motionPlanCache = new ReadThroughCacheAsync<
        RosMotionPlannerRequest,
        TaskFromRobotGoalActionResult
      >({
        getValue: (request: RosMotionPlannerRequest) =>
          this.createPlanAction(request),
        getCacheKey: (req) => getRosMotionPlanCacheKey(req),
        cacheBackend: motionPlanCacheBackend,
      });

      const inferredBucketSize =
        options.bucketSize ??
        Math.max(DEFAULT_BUCKET_SIZE, motionPlanCacheBackend.size());

      this.motionPlanBucketCache = new LRUCache<
        string,
        LRUCache<string, MotionPlanBucketEntry>
      >({
        max: inferredBucketSize,
        dispose: (bucketEntries, _, reason) => {
          if (reason === 'evict') {
            this.onBucketEvicted(bucketEntries);
          }
        },
      });

      this.initializeBucketCacheFromBackend(motionPlanCacheBackend);
    } else {
      this.motionPlanCache = null;
      this.motionPlanBucketCache = null;
    }
  }

  private forwardKinematicsCache = new LRUCache<
    string,
    ForwardKinematicsResponse
  >({ max: 50 });

  public async forwardKinematics(
    request: ForwardKinematicsRequest,
  ): Promise<ForwardKinematicsResponse> {
    const formatPosition = (position?: { x: number; y: number; z: number }) =>
      [position?.x ?? 0, position?.y ?? 0, position?.z ?? 0]
        .map((value) => value.toFixed(4))
        .join('|');

    const key = [
      request.jointAngles.map((angle) => angle.toFixed(4)).join('|'),
      request.checkValidity ? '1' : '0',
      request.gripperOpenness.toFixed(2),
      request.checkEeBounds ? '1' : '0',
      request.checkBodyBounds ? '1' : '0',
      formatPosition(request.eePositionMin),
      formatPosition(request.eePositionMax),
      formatPosition(request.bodyPositionMin),
      formatPosition(request.bodyPositionMax),
    ].join(':');

    const cachedKinematics = this.forwardKinematicsCache.get(key);

    if (cachedKinematics) {
      return cachedKinematics;
    }

    const jointState = {
      header: {
        stamp: { sec: 0, nanosec: 0 },
        frame_id: 'world',
      },
      name: ['joint0', 'joint1', 'joint2', 'joint3', 'joint4', 'joint5'],
      position: request.jointAngles,
      velocity: [],
      effort: [],
    };

    const fk_service = new FKCheckService(jointState, request.checkValidity, {
      checkEeBounds: request.checkEeBounds ?? false,
      checkBodyBounds: request.checkBodyBounds ?? false,
      eePositionMin: request.eePositionMin,
      eePositionMax: request.eePositionMax,
      bodyPositionMin: request.bodyPositionMin,
      bodyPositionMax: request.bodyPositionMax,
    });

    const error = new Error();

    try {
      const response = await fk_service.call();

      if (!response.success) {
        throw new Error(
          `ROS2 Forward kinematics failed on joint state: ${JSON.stringify(jointState)}`,
        );
      }

      const pose: CartesianPose = ros2TooltipToTarget({
        x: response.pose.position.x,
        y: response.pose.position.y,
        z: response.pose.position.z,
        i: response.pose.orientation.x,
        j: response.pose.orientation.y,
        k: response.pose.orientation.z,
        w: response.pose.orientation.w,
      });

      const collisions = (response.collisions ?? []).map(
        (collision: RCL.standard_bots_msgs.msg.Collision) => ({
          elementNames: [collision.body_1, collision.body_2].sort() as [
            string,
            string,
          ],
        }),
      );

      const result: ForwardKinematicsResponse = {
        pose,
        collisions,
        isColliding: response.in_collision,
        isEeOutOfBounds: response.is_ee_out_of_bounds ?? false,
        isBodyOutOfBounds: response.is_body_out_of_bounds ?? false,
      };

      this.forwardKinematicsCache.set(key, result);

      return result;
    } catch (e) {
      error.message = `Forward kinematics failed: ${e?.message}`;
      throw error;
    }
  }

  /**
   * Drop all cached FK responses. Call when scene-affecting state (e.g. the
   * planning-scene ACM) changes so the next FK round-trips to the planner
   * instead of returning a result computed against the old scene.
   */
  public clearForwardKinematicsCache(): void {
    this.forwardKinematicsCache.clear();
  }

  public async inverseKinematics(
    request: InverseKinematicsRequest,
  ): Promise<Array<InverseKinematicsSolution>> {
    const error = new Error();
    const tooltipPose = targetToROS2Tooltip(request.pose);

    const goal_pose: RCL.geometry_msgs.msg.Pose = {
      position: {
        x: tooltipPose.x,
        y: tooltipPose.y,
        z: tooltipPose.z,
      },
      orientation: {
        x: tooltipPose.i,
        y: tooltipPose.j,
        z: tooltipPose.k,
        w: tooltipPose.w,
      },
    };

    const ik_service = new IKCollisionCheckService(
      goal_pose,
      request.checkValidity,
      request.jointSeed,
    );

    const response = await ik_service.call();

    if (!response.success) {
      error.message = 'Inverse kinematics failed';
      throw error;
    }

    const jointPositions = Array.from(response.ik_solution.position).slice(
      0,
      6,
    );

    // Validate and parse the joint positions
    let jointAngles: ArmJointPositions;

    try {
      jointAngles = ArmJointPositions.parse(jointPositions);
    } catch (e) {
      error.message = `Failed to parse joint positions: ${e.message}`;
      throw error;
    }

    const solution: InverseKinematicsSolution = {
      jointAngles,
      collisions: [], // IKCollisionCheck_Response doesn't provide detailed collision information
      isColliding: response.in_collision,
      violatesLimits: false, // IKCollisionCheck_Response doesn't provide joint limit information
      limitViolations: new Array(6).fill(null), // Assuming 6 joints, fill with null as this info is not provided
    };

    return [solution];
  }

  private createPlanAction(
    request: RosMotionPlannerRequest,
  ): CallableCancellableAction<TaskFromRobotGoalActionResult> {
    const planAction: CallableCancellableAction<TaskFromRobotGoalActionResult> =
      request.useCartesianPlanner
        ? new CartesianPlanningAction(request as CartesianPlanningActionRequest)
        : new TaskFromRobotGoalAction(
            request as TaskFromRobotGoalActionRequest,
          );

    return {
      call: async () => {
        const planResult = await planAction.call();

        return TaskFromRobotGoalActionResult.parse(planResult);
      },
      cancel: () => planAction.cancel(),
    };
  }

  public async getMotionPlan(
    request: RosMotionPlannerRequest,
    useCache = true,
    useScalingForSpeedLimits = false,
  ): Promise<TaskFromRobotGoalActionResult> {
    if (!this.motionPlanCache) {
      log.info(
        `RosMotionPlanner.getMotionPlan`,
        'cache disabled, planning fresh',
      );

      const requestWithScaling: MotionPlanCacheRequest = {
        ...request,
        useScalingForSpeedLimits,
      };

      return this.createPlanAction(requestWithScaling).call();
    }

    if (useScalingForSpeedLimits) {
      log.info(
        `RosMotionPlanner.getMotionPlan`,
        'using scaling for speed limits',
      );
    } else {
      log.info(
        `RosMotionPlanner.getMotionPlan`,
        'using individual joint limits for speed limits',
      );
    }

    const requestWithScaling: MotionPlanCacheRequest = {
      ...request,
      useScalingForSpeedLimits,
    };

    if (!useCache) {
      log.info(`RosMotionPlanner.getMotionPlan`, 'skipping cache');

      return this.motionPlanCache.getSkipCache(requestWithScaling);
    }

    const bucketMatch = this.tryGetBucketMatch(requestWithScaling);

    let plan: TaskFromRobotGoalActionResult | undefined;
    let source = 'getMotionPlan:exact-or-fresh';

    if (bucketMatch) {
      // The bucket-matched request comes from a cache key, which deliberately
      // omits the start poses (scalingFactor/startWristPose/startTooltipPose),
      // so it cannot be safely re-planned. ReadThroughCacheAsync.get() swallows
      // a deserialize miss internally and falls through to planning from
      // whatever key it was given — for a corrupt/evicted entry that means
      // re-planning from the pose-less bucket request, which crashes the
      // Cartesian planner. Catch that (and a null/undefined plan) and fall back
      // to the exact-or-fresh path keyed on the FULL requestWithScaling, which
      // carries the real start poses. (SUP-3894)
      try {
        plan = await this.motionPlanCache.get(bucketMatch.request);
      } catch (error) {
        log.warn(
          `RosMotionPlanner.getMotionPlan`,
          'bucket-matched plan failed to load; evicting stale entry and re-planning from full request',
          { error },
        );
      }

      if (plan != null) {
        source = 'getMotionPlan:bucket-match';

        const cachedPlanKey = getRosMotionPlanCacheKey(requestWithScaling);

        if (cachedPlanKey !== bucketMatch.cacheKey) {
          this.motionPlanCache.set(requestWithScaling, plan);
          this.storeRequestInBucket(requestWithScaling);
        }
      } else {
        // Stale/corrupt bucket entry: drop it so it stops matching, then fall
        // through to the exact-or-fresh path below using requestWithScaling.
        source = 'getMotionPlan:bucket-match-fallback';

        this.deleteMotionPlanCacheEntry(
          bucketMatch.cacheKey,
          `RosMotionPlanner.getMotionPlan:bucket-match-fallback`,
        );

        this.removeBucketEntry(
          bucketMatch.request,
          bucketMatch.cacheKey,
          `RosMotionPlanner.getMotionPlan:bucket-match-fallback`,
        );
      }
    }

    // No bucket match, or the bucket-matched plan failed to load: plan from the
    // FULL requestWithScaling (real start poses). Keyed differently from the
    // bucket lookup above, so this is a distinct get() — only the served-plan
    // tail (storeRequestInBucket + finalize) is shared.
    if (plan == null) {
      plan = await this.motionPlanCache.get(requestWithScaling);
      this.storeRequestInBucket(requestWithScaling);
    }

    return this.validateServedPlan(plan, requestWithScaling, source);
  }

  public getMotionPlanCancellable(
    request: RosMotionPlannerRequest,
    useCache = true,
    useScalingForSpeedLimits = false,
  ): CallableCancellableAction<TaskFromRobotGoalActionResult> {
    if (!this.motionPlanCache) {
      log.info(
        `RosMotionPlanner.getMotionPlan`,
        'cache disabled, planning fresh',
      );

      const requestWithScaling: MotionPlanCacheRequest = {
        ...request,
        useScalingForSpeedLimits,
      };

      return this.createPlanAction(requestWithScaling);
    }

    if (useScalingForSpeedLimits) {
      log.info(
        `RosMotionPlanner.getMotionPlan`,
        'using scaling for speed limits',
      );
    } else {
      log.info(
        `RosMotionPlanner.getMotionPlan`,
        'using individual joint limits for speed limits',
      );
    }

    const requestWithScaling: MotionPlanCacheRequest = {
      ...request,
      useScalingForSpeedLimits,
    };

    if (!useCache) {
      log.info(`RosMotionPlanner.getMotionPlan`, 'Skipping cache');

      return this.motionPlanCache.getCancellableSkipCache(requestWithScaling);
    }

    const bucketMatch = this.tryGetBucketMatch(requestWithScaling);

    if (bucketMatch) {
      // The bucket-matched action is created lazily inside call(): building the
      // ReadThroughCache action constructs the underlying planning action
      // eagerly, and for a corrupt/evicted Cartesian entry that re-plans from
      // the pose-less bucket request — which throws while building the planning
      // message. Constructing it inside the try keeps that throw catchable.
      let cancellable: CallableCancellableAction<TaskFromRobotGoalActionResult> | null =
        null;

      // Fallback action keyed on the FULL requestWithScaling (real start poses),
      // created lazily only when the bucket-matched plan fails to load. Created
      // lazily so the happy path does not eagerly issue a second cache lookup.
      let fallbackCancellable: CallableCancellableAction<TaskFromRobotGoalActionResult> | null =
        null;

      return {
        call: async () => {
          if (!this.motionPlanCache) {
            return this.createPlanAction(bucketMatch.request).call();
          }

          // The bucket-matched request is reconstructed from a cache key that
          // omits the start poses, so it cannot be safely re-planned. If the
          // entry is corrupt/evicted, getCancellable swallows the miss and
          // re-plans from the pose-less bucket request, crashing the Cartesian
          // planner. On failure (or a null/undefined plan), evict the stale
          // entry and re-plan from the full requestWithScaling. (SUP-3894)
          let plan: TaskFromRobotGoalActionResult | undefined;
          let source = 'getMotionPlanCancellable:bucket-match-fallback';

          try {
            cancellable = this.motionPlanCache.getCancellable(
              bucketMatch.request,
            );

            plan = await cancellable.call();
          } catch (error) {
            // A consumer cancellation (routine-stop / e-stop / preempt) surfaces
            // here as ActionCancelledError. Re-throw it so cancellation
            // propagates to the consumer instead of falling through to the
            // fallback, which would construct and run a fresh uncancelled
            // action and silently produce a new trajectory. Only genuine load
            // failures should fall through to the fallback. (SUP-3894)
            if (error instanceof ActionCancelledError) {
              throw error;
            }

            log.warn(
              `RosMotionPlanner.getMotionPlan`,
              'bucket-matched plan failed to load; evicting stale entry and re-planning from full request',
              { error },
            );
          }

          if (plan != null) {
            source = 'getMotionPlanCancellable:bucket-match';

            const cachedPlanKey = getRosMotionPlanCacheKey(requestWithScaling);

            if (
              cachedPlanKey !== bucketMatch.cacheKey &&
              this.motionPlanCache
            ) {
              this.motionPlanCache.set(requestWithScaling, plan);
              this.storeRequestInBucket(requestWithScaling);
            }
          } else {
            // Stale/corrupt bucket entry: drop it so it stops matching, then
            // re-plan via the exact-or-fresh path using requestWithScaling.
            this.deleteMotionPlanCacheEntry(
              bucketMatch.cacheKey,
              `RosMotionPlanner.getMotionPlanCancellable:bucket-match-fallback`,
            );

            this.removeBucketEntry(
              bucketMatch.request,
              bucketMatch.cacheKey,
              `RosMotionPlanner.getMotionPlanCancellable:bucket-match-fallback`,
            );

            fallbackCancellable =
              this.motionPlanCache.getCancellable(requestWithScaling);

            plan = await fallbackCancellable.call();
            this.storeRequestInBucket(requestWithScaling);
          }

          return this.validateServedPlan(plan, requestWithScaling, source);
        },
        cancel: () =>
          (fallbackCancellable ?? cancellable)?.cancel() ?? Promise.resolve(),
      };
    }

    const callableCancellableAction =
      this.motionPlanCache.getCancellable(requestWithScaling);

    return {
      call: async () => {
        const plan = await callableCancellableAction.call();
        this.storeRequestInBucket(requestWithScaling);

        return this.validateServedPlan(
          plan,
          requestWithScaling,
          'getMotionPlanCancellable:exact-or-fresh',
        );
      },
      cancel: () => callableCancellableAction.cancel(),
    };
  }

  /**
   * Max per-joint deviation from `reference` across EVERY point of the served
   * plan. A full scan, not a sample: a cyclic multi-waypoint tour returns to
   * its start, so sampled points (e.g. first/middle/last) can all sit near
   * `reference` while the tour travels radians in between — a real plan that
   * must never be mistaken for a no-op. Each point's positions are sliced to
   * the real-joint width before comparison — planner points carry trailing
   * metadata columns (scaling + per-joint vel/acc limits), so an unsliced
   * compare would length-mismatch and return Infinity.
   */
  private maxTrajectoryDeviation(
    trajectories: TaskFromRobotGoalActionResult['trajectories'],
    reference: number[],
  ): number {
    const points = trajectories.flatMap(
      (trajectory) => trajectory?.points ?? [],
    );

    if (points.length === 0) {
      return Infinity;
    }

    let max = 0;

    for (const point of points) {
      const positions = point?.positions;

      if (Array.isArray(positions)) {
        max = Math.max(
          max,
          this.maxJointDivergence(
            positions.slice(0, reference.length),
            reference,
          ),
        );
      }
    }

    return max;
  }

  private maxJointDivergence(a: number[], b: number[]): number {
    if (a.length !== b.length) {
      return Infinity;
    }

    return a.reduce(
      (max, value, index) => Math.max(max, Math.abs(value - b[index])),
      0,
    );
  }

  /**
   * The joint-space goal of a request: the joint angles its final target
   * resolves to. Available only when the final target is a joint target;
   * Cartesian targets return null (no joint-space goal without running FK).
   */
  private getRequestGoalJoints(
    request: MotionPlanCacheRequest,
  ): number[] | null {
    const { targets } = request;

    if (!Array.isArray(targets) || targets.length === 0) {
      return null;
    }

    const finalTarget = targets[targets.length - 1] as {
      jointAngles?: unknown;
    };

    return Array.isArray(finalTarget?.jointAngles)
      ? (finalTarget.jointAngles as number[])
      : null;
  }

  /**
   * A static, single-point "hold in place" plan at `positions`. Commanding it
   * produces no motion (zero velocities/accelerations) and completes
   * immediately, so the consuming waypoint registers as reached without the
   * executor moving the arm.
   */
  private buildHoldInPlacePlan(
    positions: number[],
  ): TaskFromRobotGoalActionResult {
    const zeros = positions.map(() => 0);

    return {
      success: true,
      trajectories: [
        {
          header: { stamp: { sec: 0, nanosec: 0 }, frame_id: '' },
          joint_names: JOINT_NAMES.slice(0, positions.length),
          points: [
            {
              positions: [...positions],
              velocities: zeros,
              accelerations: zeros,
              effort: [],
              time_from_start: { sec: 0, nanosec: 0 },
            },
          ],
        },
      ],
    };
  }

  /**
   * Log cache stats, then validate that a served (cached or bucket-matched)
   * plan actually goes from the requested start to the requested goal. A stale
   * or corrupted cache entry whose trajectory begins or ends somewhere else
   * would command a discontinuous motion when executed (the vizbot "snap"; on
   * hardware, an unsafe move). On divergence we discard the cached plan,
   * re-plan fresh, and overwrite the poisoned entry so the corruption does not
   * persist.
   */
  private async validateServedPlan(
    plan: TaskFromRobotGoalActionResult,
    request: MotionPlanCacheRequest,
    source: string,
  ): Promise<TaskFromRobotGoalActionResult> {
    log.info(
      `RosMotionPlanner.getMotionPlan`,
      'planMotion cache stats',
      this.getCacheStats(),
    );

    if (!this.motionPlanCache) {
      return plan;
    }

    const trajectories = plan?.trajectories;

    if (!Array.isArray(trajectories) || trajectories.length === 0) {
      return plan;
    }

    const firstPoint = trajectories[0]?.points?.[0]?.positions;
    const finalTrajectory = trajectories[trajectories.length - 1];

    const lastPoint =
      finalTrajectory?.points?.[finalTrajectory.points.length - 1]?.positions;

    const requestStart = Array.isArray(request.startJointState)
      ? request.startJointState
      : null;

    const requestGoal = this.getRequestGoalJoints(request);

    // Planner trajectories carry trailing metadata "joints" (a scaling column
    // plus per-joint velocity/acceleration limits) that ros2-control-bot strips
    // before execution, so a point's `positions` is wider than the real-joint
    // count. The request's start/goal are real-joints-only, so compare against
    // just the leading real-joint columns; otherwise the length mismatch makes
    // maxJointDivergence return Infinity and every served plan falsely re-plans.
    const startDivergence =
      requestStart && Array.isArray(firstPoint)
        ? this.maxJointDivergence(
            firstPoint.slice(0, requestStart.length),
            requestStart,
          )
        : 0;

    const goalDivergence =
      requestGoal && Array.isArray(lastPoint)
        ? this.maxJointDivergence(
            lastPoint.slice(0, requestGoal.length),
            requestGoal,
          )
        : 0;

    // Deviation of the served plan (every point) from the live current
    // position.
    const deviationFromCurrent =
      requestStart != null
        ? this.maxTrajectoryDeviation(trajectories, requestStart)
        : Infinity;

    // A served plan that stays within tolerance of current, but whose first
    // point is off the live current position, is the SUP-4072 mis-anchor: a
    // bucket-matched plan begins at its cached start (up to startJointTolerance
    // off current), so executing it commands a step with no meaningful net
    // motion, and re-issuing it every cycle shakes the arm. Hold in place at the
    // current position instead. Covers the Cartesian path the joint-only
    // no-motion short-circuit cannot (the trajectory is joint-space regardless).
    //
    // Gate on goalDivergence too: a far-goal request served a plan whose points
    // all sit near current does NOT reach the goal — that is a stale/corrupt
    // plan to re-plan, not a no-op to hold. For a joint goal a far goal makes
    // goalDivergence large; for a pose goal it is 0 (no joint goal to check), so
    // the all-near-current trajectory is itself the no-op evidence.
    if (
      requestStart != null &&
      deviationFromCurrent <= this.startJointTolerance &&
      goalDivergence <= this.startJointTolerance &&
      startDivergence > START_STEP_ANCHORED_FLOOR_RAD
    ) {
      log.info(
        `RosMotionPlanner.validateServedPlan`,
        'served trajectory stays within tolerance of current with an off-anchor start; holding in place instead of commanding a micro-step',
        {
          source,
          startDivergence: Number(startDivergence.toFixed(4)),
          goalDivergence: Number(goalDivergence.toFixed(4)),
          deviationFromCurrent: Number(deviationFromCurrent.toFixed(4)),
          threshold: this.startJointTolerance,
        },
      );

      return this.buildHoldInPlacePlan(Array.from(requestStart));
    }

    if (
      startDivergence <= SERVED_PLAN_DIVERGENCE_TOLERANCE &&
      goalDivergence <= SERVED_PLAN_DIVERGENCE_TOLERANCE
    ) {
      return plan;
    }

    log.warn(
      `RosMotionPlanner.validateServedPlan`,
      'served plan diverges from requested start/goal; re-planning fresh',
      {
        source,
        startDivergence: Number(startDivergence.toFixed(4)),
        goalDivergence: Number(goalDivergence.toFixed(4)),
      },
    );

    const freshPlan = await this.motionPlanCache
      .getCancellableSkipCache(request)
      .call();

    this.motionPlanCache.set(request, freshPlan);

    return freshPlan;
  }

  private tryGetBucketMatch(
    request: MotionPlanCacheRequest,
  ): MotionPlanBucketEntry | null {
    if (!this.motionPlanBucketCache) {
      return null;
    }

    if (!Array.isArray(request.startJointState)) {
      return null;
    }

    this.bucketGetCount += 1;

    const bucketKey = getMotionPlanBucketKey(request);
    const bucketEntries = this.motionPlanBucketCache.get(bucketKey);

    // print md5 sum of bucket key
    const md5 = createHash('md5').update(bucketKey).digest('hex');
    log.info(`RosMotionPlanner.tryGetBucketMatch`, 'bucketKey md5', md5);

    if (!bucketEntries || bucketEntries.size === 0) {
      log.info(`RosMotionPlanner.tryGetBucketMatch`, 'no bucket entries found');

      return null;
    }

    const startJointState = Array.from(request.startJointState);

    let matchedCacheKey: string | null = null;

    for (const [entryCacheKey, entry] of bucketEntries.entries()) {
      // Type guard to ensure entry is MotionPlanBucketEntry
      if (
        entry &&
        typeof entry === 'object' &&
        'request' in entry &&
        'cacheKey' in entry &&
        Array.isArray(entry.request.startJointState) &&
        this.isWithinStartJointTolerance(
          entry.request.startJointState,
          startJointState,
        )
      ) {
        matchedCacheKey = entryCacheKey as string;
        break;
      }
    }

    if (!matchedCacheKey) {
      log.info(`RosMotionPlanner.tryGetBucketMatch`, 'no matching entry found');

      return null;
    }

    const matchedEntry = bucketEntries.get(matchedCacheKey);

    if (!matchedEntry) {
      log.info(
        `RosMotionPlanner.tryGetBucketMatch`,
        'matching entry missing after lookup',
      );

      return null;
    }

    log.info(`RosMotionPlanner.tryGetBucketMatch`, 'matching entry found');

    this.bucketHitCount += 1;

    return matchedEntry;
  }

  private storeRequestInBucket(request: MotionPlanCacheRequest): void {
    if (!this.motionPlanBucketCache) {
      return;
    }

    if (!Array.isArray(request.startJointState)) {
      return;
    }

    const entry: MotionPlanBucketEntry = {
      cacheKey: getRosMotionPlanCacheKey(request),
      request: this.cloneRequestForBucket(request),
    };

    const bucketKey = getMotionPlanBucketKey(request);

    this.upsertBucketEntry(bucketKey, entry);
  }

  private upsertBucketEntry(
    bucketKey: string,
    entry: MotionPlanBucketEntry,
  ): void {
    if (!this.motionPlanBucketCache) {
      return;
    }

    let bucketEntries = this.motionPlanBucketCache.get(bucketKey);

    if (!bucketEntries) {
      bucketEntries = this.createBucketEntryCache();
      this.motionPlanBucketCache.set(bucketKey, bucketEntries);
    }

    bucketEntries.set(entry.cacheKey, entry);
  }

  private createBucketEntryCache(): LRUCache<string, MotionPlanBucketEntry> {
    return new LRUCache<string, MotionPlanBucketEntry>({
      max: DEFAULT_BUCKET_ENTRY_CAPACITY,
      dispose: (_, cacheKey, reason) => {
        if (reason === 'evict') {
          this.deleteMotionPlanCacheEntry(
            cacheKey,
            `RosMotionPlanner.bucketEntryEvicted`,
          );
        }
      },
    });
  }

  private initializeBucketCacheFromBackend(
    backend: CacheBackend<TaskFromRobotGoalActionResult>,
  ): void {
    if (!this.motionPlanBucketCache || typeof backend.keys !== 'function') {
      return;
    }

    try {
      const cacheKeys = Array.from(backend.keys());

      for (const cacheKey of cacheKeys) {
        if (typeof cacheKey !== 'string') {
          continue;
        }

        const restoredRequest = this.restoreRequestFromCacheKey(cacheKey);

        if (!restoredRequest) {
          continue;
        }

        // Seed the bucket index from cache-key metadata only. Deliberately use
        // has() rather than get(): the bucket entry needs just the cacheKey and
        // request, both derived from the key string above, and get() would
        // deserialize the entire trajectory (JSON.parse + zod) for every cached
        // plan synchronously here in the constructor. On robots with a large
        // motion_plan_cache that stalls the event loop long enough to miss
        // heartbeats, killing and restarting the worker into the same cache —
        // a crash-loop that ratchets the heap into OOM (SUP-3894). The plan is
        // deserialized lazily on first real get() instead.
        if (!backend.has(cacheKey)) {
          continue;
        }

        const canonicalKey = getRosMotionPlanCacheKey(restoredRequest);

        const bucketKey = getMotionPlanBucketKey(restoredRequest);

        // restoredRequest is freshly constructed from JSON.parse(cacheKey)
        // inside restoreRequestFromCacheKey and has no external aliases, so the
        // bucket entry can hold it directly without an extra defensive clone.
        this.upsertBucketEntry(bucketKey, {
          cacheKey: canonicalKey,
          request: restoredRequest,
        });
      }
    } catch (error) {
      log.warn(
        `RosMotionPlanner.initializeBucketCacheFromBackend`,
        'failed to seed bucket cache',
        error,
      );
    }
  }

  private onBucketEvicted(
    bucketEntries?: LRUCache<string, MotionPlanBucketEntry>,
  ): void {
    if (!bucketEntries) {
      return;
    }

    for (const entry of bucketEntries.values()) {
      // Type guard to ensure entry is MotionPlanBucketEntry
      if (entry && typeof entry === 'object' && 'cacheKey' in entry) {
        this.deleteMotionPlanCacheEntry(
          entry.cacheKey,
          `RosMotionPlanner.onBucketEvicted`,
        );
      }
    }
  }

  private deleteMotionPlanCacheEntry(
    cacheKey: string,
    logContext: string,
  ): void {
    if (!this.motionPlanCache) {
      return;
    }

    try {
      this.motionPlanCache.deleteByCacheKey(cacheKey);
    } catch (error) {
      log.debug(logContext, 'failed to evict entry-level cache key', {
        cacheKey,
        error,
      });
    }
  }

  // Remove a single entry from the bucket index so tryGetBucketMatch stops
  // returning it. The inner LRU's dispose side effect (a backend delete) only
  // fires on 'evict', not on explicit delete(), so this won't double-delete
  // the backing plan. If the inner bucket is left empty, drop the bucketKey
  // from the outer cache too. (SUP-3894)
  private removeBucketEntry(
    request: MotionPlanCacheRequest,
    cacheKey: string,
    logContext: string,
  ): void {
    if (!this.motionPlanBucketCache) {
      return;
    }

    try {
      const bucketKey = getMotionPlanBucketKey(request);
      const bucketEntries = this.motionPlanBucketCache.get(bucketKey);

      if (!bucketEntries) {
        return;
      }

      bucketEntries.delete(cacheKey);

      if (bucketEntries.size === 0) {
        this.motionPlanBucketCache.delete(bucketKey);
      }
    } catch (error) {
      log.debug(logContext, 'failed to remove bucket entry', {
        cacheKey,
        error,
      });
    }
  }

  private areJointStatesWithinTolerance(
    cachedStart: number[],
    requestedStart: number[],
    tolerance: number,
  ): boolean {
    if (cachedStart.length !== requestedStart.length) {
      return false;
    }

    return cachedStart.every(
      (cachedValue, index) =>
        Math.abs(cachedValue - requestedStart[index]) <= tolerance,
    );
  }

  private parseJointArray(value: unknown): number[] | null {
    if (Array.isArray(value)) {
      const numbers = value.map((joint) => Number(joint));

      return numbers.some((joint) => Number.isNaN(joint)) ? null : numbers;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();

      if (!trimmed.startsWith('[') || !trimmed.endsWith(']')) {
        return null;
      }

      const inner = trimmed.slice(1, -1).trim();

      if (!inner) {
        return [];
      }

      const numbers = inner.split(',').map((part) => Number(part.trim()));

      return numbers.some((joint) => Number.isNaN(joint)) ? null : numbers;
    }

    return null;
  }

  private cloneRequestForBucket(
    request: MotionPlanCacheRequest,
  ): MotionPlanCacheRequest {
    // structuredClone is V8-native (no intermediate JSON string allocation) and
    // has identical semantics to a JSON roundtrip for any JSON-shaped object.
    return structuredClone(request);
  }

  private restoreRequestFromCacheKey(
    cacheKey: string,
  ): MotionPlanCacheRequest | null {
    try {
      const parsed = JSON.parse(cacheKey);

      const startJointState = this.parseJointArray(parsed.startJointState);

      if (!startJointState) {
        return null;
      }

      parsed.startJointState = startJointState;

      if (Array.isArray(parsed.targets)) {
        parsed.targets = parsed.targets.map((target: any) => {
          if (target && typeof target === 'object' && 'jointAngles' in target) {
            const jointAngles = this.parseJointArray(target.jointAngles);

            if (jointAngles) {
              return { ...target, jointAngles };
            }
          }

          return target;
        });
      }

      if (Array.isArray(parsed.speedProfiles)) {
        parsed.speedProfiles = parsed.speedProfiles.map((profile: any) => ({
          ...profile,
        }));
      } else if (parsed.speedProfile) {
        parsed.speedProfiles = [parsed.speedProfile];
        delete parsed.speedProfile;
      } else {
        parsed.speedProfiles = [];
      }

      parsed.useScalingForSpeedLimits = Boolean(
        parsed.useScalingForSpeedLimits,
      );

      return parsed as MotionPlanCacheRequest;
    } catch (error) {
      log.debug(
        `RosMotionPlanner.restoreRequestFromCacheKey`,
        'failed to restore request from cache key',
        { error, cacheKey },
      );

      return null;
    }
  }

  private getCacheStats() {
    const hitCount = this.motionPlanCache?.hitCount ?? 0;
    const getCount = this.motionPlanCache?.getCount ?? 0;
    const cacheSize = this.motionPlanCache?.size() ?? 0;
    const bucketSize = this.motionPlanBucketCache?.size ?? 0;

    const hitRate = getCount === 0 ? '0' : (hitCount / getCount).toPrecision(4);

    const bucketHitRate =
      this.bucketGetCount === 0
        ? '0'
        : (this.bucketHitCount / this.bucketGetCount).toPrecision(4);

    return {
      hitCount,
      getCount,
      hitRate,
      cacheSize,
      bucketHitCount: this.bucketHitCount,
      bucketGetCount: this.bucketGetCount,
      bucketHitRate,
      bucketSize,
    };
  }

  public async getRecoveryMotionPlan(): Promise<
    RCL.trajectory_msgs.msg.JointTrajectory[]
  > {
    const recovery_service = new RecoveryService();
    const error = new Error();

    try {
      const response = await recovery_service.call();

      if (!response.success) {
        throw new Error('Recovery service call was not successful');
      }

      return [response.recovery_trajectory];
    } catch (e) {
      error.message = `Recovery motion plan failed: ${e.message}`;
      throw error;
    }
  }

  public clearMotionPlanCache() {
    if (this.motionPlanCache) {
      this.motionPlanCache.reset();
    }

    if (this.motionPlanBucketCache) {
      this.motionPlanBucketCache.clear();
    }

    this.bucketGetCount = 0;
    this.bucketHitCount = 0;
  }

  public getJogAction(
    jogActionRequest: JoggingActionRequest,
    useScalingForSpeedLimits = false,
  ): JoggingAction {
    // Create a copy of the request to avoid modifying the original
    if (useScalingForSpeedLimits) {
      log.info(
        `RosMotionPlanner.getJogAction`,
        'using scaling for speed limits',
      );
    } else {
      log.info(
        `RosMotionPlanner.getJogAction`,
        'using individual joint limits for speed limits',
      );
    }

    const jogAction = new JoggingAction({
      ...jogActionRequest,
      useScalingForSpeedLimits,
    });

    return jogAction;
  }

  public async getToolDirections(
    jointPositions: number[],
    translationDistance: number,
    rotationAngle: number,
    frameOfReference: FrameOfReference,
  ): Promise<FrameDirections> {
    const service = new GetToolDirectionsService(
      jointPositions,
      translationDistance,
      rotationAngle,
      frameOfReference,
    );

    return await service.getResult();
  }
}
